import { Component, inject, QueryList, ViewChildren } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ApiAbbreviatedArticle, ApiArticle, ApiLiveStream, ApiProduction, ApiShow, ApiTuneInProvider, ApiUser } from '@tytapp/api';
import { ArticlesService } from '@tytapp/articles';
import { BillingService } from '@tytapp/billing';
import { CanonicalURLService, PageComponent, PagedArray, Transfer } from '@tytapp/common';
import { ProductionsService } from '@tytapp/common-ui';
import { isClientSide } from '@tytapp/environment-utils';
import { LiveStreamsService } from '@tytapp/live-streams';
import { ContentAvailability, ShowsService } from '@tytapp/shows';
import { UserService } from '@tytapp/user';
import { combineLatest } from 'rxjs';

import { isPlaylist, Playlist, PLAYLIST_TYPES } from '@tytapp/media-playback';
import stringDistance from 'js-levenshtein';

interface Tab {
    id: string;
    label: string;
    url: string;
}

interface Page {
    total: number;
    page: number;
}

interface PageMap {
    [section: string]: Page;
}

const VALID_SECTIONS = [...PLAYLIST_TYPES, 'content', 'about', 'articles'] as const;
type Section = (typeof VALID_SECTIONS)[number];

@Component({
    selector: 'tyt-show',
    templateUrl: './show.component.html',
    styleUrls: ['./show.component.scss']
})
export class ShowComponent extends PageComponent {
    private userService = inject(UserService);
    private billing = inject(BillingService);
    private articlesService = inject(ArticlesService);
    private productionsService = inject(ProductionsService);
    private showsService = inject(ShowsService);
    private liveStreamsService = inject(LiveStreamsService);
    private canonicalService = inject(CanonicalURLService);

    get pageComponentId() {
        return 'show';
    }

    @Transfer()
    user: ApiUser;
    @Transfer()
    entitled = false;
    @Transfer()
    section: string = 'vods';

    @Transfer()
    state: 'normal' | 'production-not-found' | 'show-not-found' | 'loading' | 'production' = 'normal';

    @Transfer()
    web_url: string;
    @Transfer()
    twitter_handle: string;
    @Transfer()
    youtube_handle: string;
    @Transfer()
    facebook_handle: string;
    @Transfer()
    instagram_handle: string;

    @Transfer()
    clips: PagedArray<ApiProduction>;
    @Transfer()
    episodes: PagedArray<ApiProduction>;
    @Transfer()
    podcasts: PagedArray<ApiProduction>;

    @Transfer()
    productions: PagedArray<ApiProduction>;

    @Transfer()
    articles: PagedArray<ApiAbbreviatedArticle>;
    @Transfer()
    tabs: Tab[] = [];

    @Transfer()
    page: Page = null;
    @Transfer()
    pageMap: PageMap = {};
    @Transfer()
    pageSize: number = 10;
    @Transfer()
    activeLiveStreams: ApiLiveStream[];

    @Transfer()
    production: ApiProduction;
    @Transfer()
    productionMode: Playlist | 'content';

    @Transfer()
    show: ApiShow;

    @Transfer()
    mostRecent: ApiProduction;

    @ViewChildren(MatTabGroup)
    tabGroups: QueryList<MatTabGroup>;

    @Transfer()
    requestedSection: Section;

    isPageableSection(section: string) {
        return section !== 'about' && section !== 'production';
    }

    private sessionFetchTimeout: any;

    async switchSection(section: string, page?: number) {
        if (['episodes', 'podcasts', 'clips'].includes(section))
            section = 'content';

        this.section = section;

        // Fetch pages of data when needed:

        if (section !== 'about' && !this.isPageableSection(section))
            return;

        let pagedResult: PagedArray<any> = null;
        let isNewPage = false;

        if (!page)
            page = 1;

        let existingPage = this.pageMap[section];

        if (existingPage && page && existingPage.page != page) {
            isNewPage = true;
        } else if (!existingPage) {
            isNewPage = true;
        }

        if (!isNewPage) {
            return;
        }

        if (this.section === 'content') {
            this.productions = null;

            let availability: ContentAvailability = 'unarchived';
            await this.userService.ready;
            if (this.user?.staff) {
                availability = 'all';
            }

            if (this.requestedSection === 'episodes')
                pagedResult = this.productions = await this.showsService.getEpisodes(this.show.id, (page - 1) * this.pageSize, this.pageSize, availability);
            else if (this.requestedSection === 'clips')
                pagedResult = this.productions = await this.showsService.getClips(this.show.id, (page - 1) * this.pageSize, this.pageSize, availability);
            else if (this.requestedSection === 'podcasts')
                pagedResult = this.productions = await this.showsService.getPodcasts(this.show.id, (page - 1) * this.pageSize, this.pageSize, availability);
            else
                pagedResult = this.productions = await this.showsService.getProductions(this.show.id, (page - 1) * this.pageSize, this.pageSize, availability);

        } else if (this.section == 'articles') {
            this.articles = null;
            pagedResult = this.articles = await this.showsService.getArticles(this.show.id, (page - 1) * this.pageSize, this.pageSize);
        } else {
            this.logger.warning(`Unhandled show page section '${this.section}'!`);
        }

        if (pagedResult) {
            // Update our paging information to match the data we just queried

            this.page = {
                total: pagedResult.total,
                page: page || 1
            };

            // Record the page for later if we return to this tab
            this.pageMap[section] = this.page;
        }
    }

    get activeLiveStream() {
        if (!this.activeLiveStreams)
            return null;
        if (!this.show)
            return null;

        let viableStreams = this.activeLiveStreams.filter(x => x.active_live_event && x.active_live_event.show && x.active_live_event.show.id == this.show.id);
        let premiumStream = viableStreams.find(x => x.premium);
        let publicStream = viableStreams.find(x => !x.premium);

        if (this.entitled) {
            return premiumStream || publicStream;
        } else {
            return publicStream || premiumStream;
        }
    }

    audienceType: string = 'visitor';

    async fetchLiveStreams() {
        this.activeLiveStreams = await this.liveStreamsService.active();
    }

    async init() {
        // Only fetch live streams if we are on the client side
        if (isClientSide()) {
            this.fetchLiveStreams();
        }

        this.subscribe(this.billing.entitlementChanged, entitled => this.entitled = entitled);
        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
        });

        let observer = combineLatest(this.route.params, this.route.queryParams, (params, queries) => ({ params, queries }));
        observer.subscribe(this.update.bind(this));

        this.audienceType = this.userService.audienceType;
        this.isOpenBeta = await this.shell.hasFeature('apps.web.nation_open_beta');
    }

    private async didYouMean(showId: string): Promise<ApiShow> {
        let shows = await this.showsService.all();
        let results = [];

        for (let show of shows) {
            if (!show.slug)
                continue;

            results.push({ show, distance: stringDistance(showId, show.slug) });
        }

        results = results.sort((a, b) => a.distance - b.distance);

        let topResult = results[0];

        if (topResult.distance < 5) {
            return topResult.show;
        }

        return null;
    }

    async update({ params, queries }) {

        if (this.hasServerState)
            return;

        let showId = params.id;
        if (!this.show || this.show.id != showId) {
            // Active show has changed
            this.show = await this.showsService.get(showId) ?? await this.didYouMean(showId);
            if (!this.show) {
                this.state = 'show-not-found';
                return;
            }

            showId = this.show.slug ?? this.show.id;

            this.podcasts = null;
            this.clips = null;
            this.episodes = null;
            this.page = null;
            this.pageMap = {};

            this.pageTitle = this.show.name;
            this.setMeta({
                description: this.show.description
            });

            this.web_url = (this.show.website_url && this.show.website_url != '')
                ? this.show.website_url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
                : null
                ;

            let twitter_handle: string = null;
            if (this.show.twitter_handle) {
                twitter_handle = this.show.twitter_handle;
                twitter_handle = twitter_handle.replace(/^https?:\/\/(www\.)?twitter.com\//, '').replace(/\/+$/, '');
                twitter_handle = twitter_handle.replace(/^@/, '');
            }

            this.twitter_handle = twitter_handle;
            this.youtube_handle = this.show.youtube_url ? this.show.youtube_url.replace(/https?:\/\/(www\.)?youtube\.com/, "youtube.com").replace(/\/+$/, '') : null;
            this.facebook_handle = this.show.facebook_url ? this.show.facebook_url.replace(/https:\/\/(www\.)?facebook\.com/, "fb.com").replace(/\/+$/, '') : null;
            this.instagram_handle = this.show.instagram_handle ? this.show.instagram_handle.replace("@", "") : null;
        }

        let canonicalSection = params.section;

        if (params.section && !VALID_SECTIONS.includes(params.section)) {
            try {
                let productionID = params.section;

                if (/\d\d\d\d-\d\d-\d\d(-\d+)?/.test(productionID)) {
                    productionID = `${showId}:${productionID}`;
                }

                this.production = <ApiProduction>await this.productionsService.get(productionID);
            } catch (e) {
                if (e.json)
                    e = e.json();

                this.production = null;
            }

            if (!this.production) {
                this.state = 'production-not-found';
                return;
            }

            this.state = 'production';
            this.productionMode = params.section;
            showId = this.production.show?.slug ?? this.production.show?.id ?? '-';
            canonicalSection = this.production.date_simple ?? this.production.id;
        } else {
            // Determine the active tab and load it

            this.state = 'normal';
            let section = VALID_SECTIONS.includes(params.section) ? params.section : undefined;
            canonicalSection = section;
            if (canonicalSection === 'content')
                canonicalSection = undefined;
            this.requestedSection = section ?? 'content';
            if (isPlaylist(section))
                this.productionMode = section;
            else
                this.productionMode = 'content';

            if (!section) {
                if (this.show.default_tab) {
                    section = this.show.default_tab;
                    if (this.show.default_tab === 'episodes' && (this.audienceType === 'visitor' || this.audienceType === 'expired member') && this.show.enable_clips) {
                        section = 'clips';
                    }
                } else {
                    if (this.show.enable_vod)
                        section = 'episodes';
                    else if (this.show.enable_clips)
                        section = 'clips';
                    else if (this.show.enable_podcasting)
                        section = 'podcasts';
                    else if (this.show.enable_text_articles)
                        section = 'articles';
                    else
                        section = 'about';
                }
            }

            this.switchSection(section, queries.page);
        }

        // Canonical URLs

        await this.userService.ready;

        let canonicalUrl: string;
        if (this.show.username && this.userService.user?.can_access_tytnation) {
            canonicalUrl = `/@${this.show.username}`;
        } else if (canonicalSection) {
            canonicalUrl = `/shows/${showId}/${canonicalSection}`;
        } else {
            canonicalUrl = `/shows/${showId}`
        }

        if (this.canonicalService.redirectToCanonical(canonicalUrl))
            return;
    }

    hasItemsOfRequestedType(production: ApiProduction) {
        if (this.requestedSection === 'content')
            return production.full_length_vods?.length > 0 || production.full_length_podcasts?.length > 0 || production.vod_clips?.length > 0;
        else if (this.requestedSection === 'episodes')
            return production.full_length_vods?.length > 0;
        else if (this.requestedSection === 'podcasts')
            return production.full_length_podcasts?.length > 0;
        else if (this.requestedSection === 'clips')
            return production.vod_clips?.length > 0;

        return false;
    }

    async getRecentArticles() {
        let recentArticles: ApiArticle[] = null;
        try {
            recentArticles = await this.articlesService.all();

            return recentArticles;
        } catch (error) {
            if (error.json) {
                error = error.json();
            }

            this.logger.error(`Caught exception while fetching articles:`);
            this.logger.error(error);
            return null;
        }
    }

    goToPage(page) {
        this.router.navigate([], { queryParams: { page } });
    }

    getPodcastProviders(show: ApiShow): ApiTuneInProvider[] {
        if (!show || !show.tune_ins)
            return [];

        let podcastTuneIn = show.tune_ins.find(x => x.type == 'podcast');

        if (!podcastTuneIn)
            return [];

        return podcastTuneIn.destinations;
    }

    isOpenBeta: boolean;
    get shouldShowTalentUser() {
        if(!this.user) return false;
        if (this.isOpenBeta || (!this.isOpenBeta && this.user.can_access_tytnation)) {
            return true;
        }
        return false;
    }

    get isLastPage() {
        return this.page && Math.ceil(this.page.total / this.pageSize) == this.page.page;
    }
}
